import { useState } from 'react'
import toast from 'react-hot-toast'
import { Check, Loader2, Sparkles, Tag } from 'lucide-react'
import { formatCredits, validateCoupon, type CatalogPack } from '@/api/billing'

/**
 * The pack grid plus the buy-confirm modal.
 *
 * This modal is a CHECKOUT SURFACE, so it is the one place in the end-user UI
 * that shows money. Everything above it is credits.
 */
export default function TopUpPacks({
  packs, busy, onBuy, heading = 'Top up credits',
}: {
  packs: CatalogPack[]
  busy: boolean
  onBuy: (packCode: string, couponCode?: string) => Promise<void>
  heading?: string
}) {
  const [confirming, setConfirming] = useState<CatalogPack | null>(null)
  const [coupon, setCoupon] = useState('')
  const [couponState, setCouponState] = useState<{ ok: boolean; msg: string } | null>(null)
  const [checking, setChecking] = useState(false)

  const openConfirm = (pack: CatalogPack) => {
    setConfirming(pack)
    setCoupon('')
    setCouponState(null)
  }

  const applyCoupon = async () => {
    const code = coupon.trim()
    if (!code || !confirming) return
    setChecking(true)
    try {
      const res = await validateCoupon(code, 'packs', confirming.amount_minor)
      setCouponState(res.valid
        ? { ok: true, msg: res.description || 'Coupon applied at checkout.' }
        : { ok: false, msg: res.error || "That coupon can't be used." })
    } catch {
      setCouponState({ ok: false, msg: 'Could not check that coupon.' })
    } finally {
      setChecking(false)
    }
  }

  const confirm = async () => {
    if (!confirming) return
    const pack = confirming
    const code = couponState?.ok ? coupon.trim() : undefined
    setConfirming(null)
    try {
      await onBuy(pack.code, code)
    } catch (err) {
      toast.error((err as Error)?.message || 'Purchase failed')
    }
  }

  if (!packs.length) {
    return (
      <div className="card" style={{ padding: '1.25rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.0625rem' }}>{heading}</h3>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', margin: 0 }}>
          No credit packs are available right now. Contact your administrator.
        </p>
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <h3 style={{ margin: '0 0 0.875rem', fontSize: '1.0625rem' }}>{heading}</h3>
      <div style={{
        display: 'grid', gap: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      }}>
        {packs.map((pack) => (
          <button
            key={pack.code}
            className="card"
            disabled={busy}
            onClick={() => openConfirm(pack)}
            style={{
              padding: '1rem', textAlign: 'left', cursor: busy ? 'wait' : 'pointer',
              border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{pack.name}</div>
            <div style={{ fontSize: '1.5625rem', fontWeight: 700, lineHeight: 1.1 }}>
              {formatCredits(pack.total_credits)}
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}> credits</span>
            </div>
            {pack.bonus_credits > 0 && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                fontSize: '0.8125rem', color: 'var(--color-success, #2e9e5b)', marginTop: '0.25rem',
              }}>
                <Sparkles size={12} /> includes {formatCredits(pack.bonus_credits)} bonus
              </div>
            )}
            {/* Price on the card is deliberate: this grid IS a checkout surface. */}
            <div style={{ marginTop: '0.5rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
              {pack.display}
            </div>
          </button>
        ))}
      </div>

      {confirming && (
        <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirming(null) }}>
          <div className="modal animate-slide-up" style={{ maxWidth: 420, width: '95%' }}>
            <div className="modal-header">
              <h3 className="modal-title">Buy {confirming.name}</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{
                background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', padding: '0.75rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9375rem' }}>
                  <span>Credits</span>
                  <strong>{formatCredits(confirming.credits)}</strong>
                </div>
                {confirming.bonus_credits > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9375rem', marginTop: '0.25rem' }}>
                    <span>Bonus</span>
                    <strong style={{ color: 'var(--color-success, #2e9e5b)' }}>
                      +{formatCredits(confirming.bonus_credits)}
                    </strong>
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem',
                  paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)', fontWeight: 700,
                }}>
                  <span>Total</span>
                  <span>{confirming.display}</span>
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.375rem' }}>
                  Taxes are calculated at checkout and shown on your invoice.
                </div>
              </div>

              <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Coupon code (optional)
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <input
                    value={coupon}
                    onChange={(e) => { setCoupon(e.target.value); setCouponState(null) }}
                    placeholder="e.g. WELCOME20"
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-secondary" onClick={applyCoupon} disabled={checking || !coupon.trim()}>
                    {checking ? <Loader2 size={14} className="spin" /> : <Tag size={14} />} Apply
                  </button>
                </div>
              </label>
              {couponState && (
                <div style={{
                  fontSize: '0.8125rem',
                  color: couponState.ok ? 'var(--color-success, #2e9e5b)' : 'var(--color-danger, #d64545)',
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                }}>
                  {couponState.ok && <Check size={12} />} {couponState.msg}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setConfirming(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={confirm} disabled={busy}>
                  Pay {confirming.display}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
